import React, { useState, useEffect } from "react";
import { Menu, X, Phone, Facebook, Search, Clock } from "lucide-react";
import { Button } from "/components/Buttons";
import useSiteSettings, { splitPhones } from "utils/useSiteSettings";
import BrandLogo from "components/BrandLogo";

const NAV = [
  { id: "track", label: "Илгээмж шалгах" },
  { id: "how", label: "Хэрхэн ажилладаг" },
  { id: "services", label: "Үйлчилгээ" },
  { id: "branches", label: "Салбарууд" },
  { id: "contact", label: "Холбоо барих" },
];

const Header = () => {
  const { get } = useSiteSettings();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const phones = splitPhones(get("footer.phone"));
  const fb = get("services.facebookUrl");
  // Цагийн хуваарийн эхний мөрийг дээд зурваст харуулна
  const hours = String(get("footer.hours") || "").split("\n")[0].trim();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Дээд мэдээллийн зурвас (утас / цаг / facebook) */}
      <div className="hidden md:block bg-accent text-accent-foreground text-xs">
        <div className="container mx-auto px-4 h-9 flex items-center justify-between">
          <div className="flex items-center gap-5">
            {phones.map((p) => (
              <a key={p} href={`tel:${p}`} className="inline-flex items-center gap-1.5 font-semibold hover:text-brand-blue transition-colors">
                <Phone className="w-3.5 h-3.5" /> {p}
              </a>
            ))}
            {hours && (
              <span className="inline-flex items-center gap-1.5 text-accent-foreground/80">
                <Clock className="w-3.5 h-3.5" /> {hours}
              </span>
            )}
          </div>
          {fb && (
            <a href={fb} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-semibold hover:text-brand-blue transition-colors">
              <Facebook className="w-3.5 h-3.5" /> Facebook group
            </a>
          )}
        </div>
      </div>

      {/* Үндсэн толгой */}
      <div
        className={`w-full border-b transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-border" : "bg-white border-transparent"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-[68px]">
            {/* Brand */}
            <a href="#top" onClick={go("top")} className="flex items-center gap-2.5 shrink-0">
              <BrandLogo />
            </a>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((n) => (
                <a
                  key={n.id}
                  href={`#${n.id}`}
                  onClick={go(n.id)}
                  className="px-4 py-2 rounded-full text-sm font-semibold text-foreground/75 hover:text-brand-blue hover:bg-brand-blue/5 transition-colors"
                >
                  {n.label}
                </a>
              ))}
            </nav>

            {/* Right action */}
            <div className="hidden md:flex items-center">
              <Button
                onClick={go("track")}
                className="rounded-xl bg-brand-blue hover:bg-brand-blue/90 text-white font-bold px-5 shadow-lg shadow-brand-blue/25"
              >
                <Search className="w-4 h-4" /> Илгээмж шалгах
              </Button>
            </div>

            {/* Mobile toggle */}
            <button
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-xl text-foreground hover:bg-muted"
              onClick={() => setOpen((v) => !v)}
              aria-label="Цэс"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden border-t border-border bg-white">
            <nav className="container mx-auto px-4 py-3 flex flex-col gap-1">
              {NAV.map((n) => (
                <a
                  key={n.id}
                  href={`#${n.id}`}
                  onClick={go(n.id)}
                  className="px-3 py-2.5 text-sm font-semibold text-foreground/90 rounded-xl hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {n.label}
                </a>
              ))}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 pt-3 border-t border-border mt-2">
                {phones.map((p) => (
                  <a key={p} href={`tel:${p}`} className="inline-flex items-center gap-2 text-sm font-bold text-brand-blue">
                    <Phone className="w-4 h-4" /> {p}
                  </a>
                ))}
                {fb && (
                  <a href={fb} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-bold text-brand-blue">
                    <Facebook className="w-4 h-4" /> Facebook
                  </a>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
